import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { GroupType, PatientTherapySequenceStatus } from '../database/schema.js';
import { EventLogResources } from '../mutators/util.js';
import { applyCursor } from '../util/apply-cursor.js';
import { assertIsAuthenticated, EventLogs, getApplicableSequenceStatusesForGroups, isPartOfGroup } from '../utils.js';
import { zql } from '../zero-schema.gen.js';
import { Cursor, Sort } from './util.js';
import { DispatchMode } from '../schema.js';
export const ProductionStatusKey = z.enum([
    'production_planning',
    'in_production',
    'qa_dispatch',
    'discarded',
    'validation_queue',
    // 'awaiting_release' only applies while the `doctorRelease` setting keeps the arrival step enabled.
    'awaiting_release',
]);
export const ListPatientTherapySequencesFilters = z.object({
    search: z.string().optional(),
    status: z.array(PatientTherapySequenceStatus).optional(),
    productionStatus: ProductionStatusKey.optional(),
    wards: z.array(z.uuidv7()).optional(),
    sections: z.array(z.uuidv7()).optional(),
    responsibleDoctors: z.array(z.uuidv7()).optional(),
    therapyDate: z.array(z.iso.date()).optional(),
    onlyCurrentUserActions: z.boolean().optional(),
    // Whether the patient-arrival step is enabled (`doctorRelease`). When false, the doctor/nurse "pending arrival" action must not be counted; the client passes this from the settings row.
    patientArrivalEnabled: z.boolean().optional(),
    patientId: z.string().optional(),
});
export const ListPatientTherapySequencesInput = z.object({
    filters: ListPatientTherapySequencesFilters.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number().nullable(),
        therapyDate: z.number().nullable(),
        time: z.string().optional(),
        medicineNames: z.string().nullable().optional(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
export const ListOrdersFilters = z.object({
    productionLocationId: z.array(z.uuidv7()).optional(),
    therapyDateFrom: z.number().optional(),
    dispatchMode: DispatchMode.optional(),
});
export const ListOrdersInput = z.object({
    filters: ListOrdersFilters.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
export function getSequenceMedicines(medicine) {
    return medicine
        .related('activeSubstance', drug => drug.related('activeSubstanceDetails', activeSubstance => activeSubstance.related('doseUnit')))
        .related('prescribedName', prescribedName => prescribedName
        // the dose unit lets the picking list convert vial strengths into the unit of totalAmount
        .related('drugCatalogItem', drug => drug
        .related('activeSubstanceDetails', details => details.related('doseUnit'))
        .related('supportiveMedicationDetails', details => details.related('doseUnit')))
        // trade-name strength/reconstitution data drives the live concentration preview
        .related('tradeNames', tn => tn
        .where('deletedAt', 'IS', null)
        .related('reconstitutedConcentrationUnit')
        .related('reconstitutionSolvents', rs => rs.where('deletedAt', 'IS', null).related('solutionDrug', drug => drug.where('deletedAt', 'IS', null)))));
}
function populateTherapySequence(q) {
    return q
        .related('patient')
        .related('applicationForm')
        .related('carrierSolution')
        .related('consumables')
        .related('protocol', protocol => protocol.related('diagnosis'))
        .related('medicine', getSequenceMedicines)
        .related('observations', obs => obs.where('deletedAt', 'IS', null));
}
/**
 * A `limit` inside a related query is a partitioned take, which Zero refuses to fetch on demand
 * (`zero.run`), so the queries read that way drop it and take the first row in code.
 */
function withLatestOnly(query, latestOnly) {
    return latestOnly ? query.limit(1) : query;
}
/** The prescription graph the side drawer and the Reports reprints read (CY-87). */
function withSequenceGraph({ latestOnly = true } = {}) {
    return (zql.patientTherapySequence
        .related('patient', patient => patient
        .related('characteristics', c => withLatestOnly(c.where('deletedAt', 'IS', null).orderBy('createdAt', 'desc'), latestOnly).related('diagnosis'))
        // same rule as inUseGfrQuery: validation must recalculate with the GFR the pop-up showed
        .related('gfrs', g => withLatestOnly(g.where('deletedAt', 'IS', null).orderBy('labTime', 'desc').orderBy('createdAt', 'desc'), latestOnly)))
        .related('applicationForm')
        .related('carrierSolution')
        .related('consumables')
        .related('protocol', protocol => protocol.related('diagnosis'))
        .related('medicine', getSequenceMedicines)
        .related('pharmacistValidated')
        .related('doctorValidated')
        .related('observations', obs => obs.related('created', creator => creator.related('groups')).where('deletedAt', 'IS', null))
        .related('order', o => o.related('productionLocation'))
        // the lots this prescription used, not every batch of the picking list (CY-382)
        .related('batches', ob => ob.related('batch', batch => batch.related('tradeName'))));
}
/** Everything the preparation label prints, in one graph. */
function withPreparationLabelGraph({ latestOnly = true } = {}) {
    return zql.patientTherapySequence
        .related('patient', patient => patient.related('responsibleDoctor').related('encounters', encounter => withLatestOnly(encounter.where('deletedAt', 'IS', null).orderBy('createdAt', 'desc'), latestOnly)
        .related('ward')
        .related('customer')))
        .related('applicationForm')
        .related('carrierSolution')
        .related('medicine', medicine => medicine
        .where('deletedAt', 'IS', null)
        .related('prescribedName')
        .related('activeSubstance', drug => drug
        .related('activeSubstanceDetails', details => details.related('doseUnit').related('hazardClasses', link => link.related('hazardClass')))
        // a supportive medication has its dose unit here; the label never guesses one (CY-403)
        .related('supportiveMedicationDetails', details => details.related('doseUnit'))))
        .related('order', order => order.related('productionLocation', location => location.related('pharmacy', pharmacy => pharmacy.related('address'))))
        .related('batches', orderBatch => orderBatch.related('batch', batch => batch.related('tradeName', tradeName => tradeName
        .related('manufacturer')
        .related('prescribedName')
        // solvent rows of reconstituted vials, listed on the production report
        .related('reconstitutionSolvents', rs => rs.where('deletedAt', 'IS', null).related('solutionDrug', drug => drug.where('deletedAt', 'IS', null)))
        .related('stabilityRules', link => link.where('deletedAt', 'IS', null).related('stabilityRule', rule => rule.related('storageLabel')))
        .related('medicationProfile', profile => profile.related('stabilityRules', link => link.where('deletedAt', 'IS', null).related('stabilityRule', rule => rule.related('storageLabel')))))))
        .related('productionDetails', details => details.where('deletedAt', 'IS', null).orderBy('recordedAt', 'asc'));
}
/** status-change log of a sequence; the COMPLETE_PRODUCTION entry gives produced at / prepared by */
function withStatusLogs() {
    return zql.eventLogs
        .where('resource', '=', EventLogResources.patientTherapySequence)
        .where('event', '=', EventLogs.PRESCRIPTION_STATUS_CHANGE)
        .related('actor')
        .orderBy('performedAt', 'desc');
}
export const patientsTherapiesQueries = {
    listSequences: defineQuery(ListPatientTherapySequencesInput, ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        let q = zql.patientTherapySequence.where('deletedAt', 'IS', null).orderBy('therapyDate', 'asc');
        // sorting by medicine names replaces the time ordering but stays within the therapy day so day headers keep grouping
        if (args.sort.field !== 'medicineNames')
            q = q.orderBy('time', 'asc');
        if (args.filters) {
            const search = args.filters.search?.trim()?.toLowerCase();
            const { status, productionStatus, therapyDate, wards, sections, onlyCurrentUserActions, responsibleDoctors, patientId, } = args.filters;
            if (search) {
                const pattern = `%${search}%`;
                // the prescription number lets staff find a preparation from its label
                q = q.where(({ or, cmp, exists }) => or(cmp('internalId', 'ILIKE', pattern), exists('patient', patient => patient.where(({ or, cmp }) => or(cmp('fullName', 'ILIKE', pattern), cmp('familyName', 'ILIKE', pattern), cmp('givenName', 'ILIKE', pattern), cmp('hospitalPatientId', 'ILIKE', pattern))))));
            }
            if (patientId) {
                q = q.where('patientId', '=', patientId);
            }
            if (productionStatus) {
                q = q.where('isNote', '=', false);
                if (productionStatus === 'validation_queue') {
                    q = q.where(({ and, cmp }) => and(cmp('status', PatientTherapySequenceStatus.enum.validating), cmp('pharmacistValidatedAt', 'IS', null)));
                }
                else if (productionStatus === 'awaiting_release') {
                    q = q.where(({ and, cmp }) => and(cmp('status', PatientTherapySequenceStatus.enum.validating), cmp('patientArrivedAt', 'IS', null), cmp('pharmacistValidatedAt', 'IS NOT', null)));
                }
                else {
                    q = q.where('status', productionStatus);
                }
            }
            else if (status?.length) {
                q = q.where('status', 'IN', status);
            }
            else if (onlyCurrentUserActions) {
                const groups = ctx.groups || [];
                const statuses = getApplicableSequenceStatusesForGroups(groups);
                const isPharmacist = isPartOfGroup(groups, GroupType.enum.PHARMACIST);
                const isDoctor = isPartOfGroup(groups, GroupType.enum.DOCTOR);
                const isNurse = isPartOfGroup(groups, GroupType.enum.NURSE);
                const isPatientArrivalEnabled = args.filters.patientArrivalEnabled !== false;
                q = q.where(({ and, cmp, or }) => {
                    const conditions = [];
                    if (isPharmacist)
                        conditions.push(and(cmp('status', PatientTherapySequenceStatus.enum.validating), cmp('pharmacistValidatedAt', 'IS', null)));
                    // arrival is only an action while the doctorRelease setting keeps the step enabled
                    if ((isDoctor || isNurse) && isPatientArrivalEnabled)
                        conditions.push(and(cmp('status', PatientTherapySequenceStatus.enum.validating), cmp('patientArrivedAt', 'IS', null)));
                    if (statuses.length)
                        conditions.push(cmp('status', 'IN', statuses));
                    return or(...conditions);
                });
            }
            if (therapyDate?.length) {
                q = q.where('therapyDate', 'IN', therapyDate.map(date => new Date(date).getTime()));
            }
            if (wards?.length || sections?.length) {
                q = q.whereExists('patient', patient => patient.whereExists('encounters', encounters => {
                    let encountersQuery = encounters.orderBy('createdAt', 'desc').limit(1);
                    if (wards?.length)
                        encountersQuery = encountersQuery.where('wardId', 'IN', wards);
                    if (sections?.length)
                        encountersQuery = encountersQuery.where('sectionId', 'IN', sections);
                    return encountersQuery;
                }));
            }
            if (responsibleDoctors && responsibleDoctors?.length) {
                q = q.whereExists('patient', patient => patient.where('responsibleDoctorId', 'IN', responsibleDoctors));
            }
        }
        q = applyCursor(q, { cursor: args.cursor, limit: args.limit, sort: args.sort });
        q = populateTherapySequence(q);
        return q;
    }),
    // count/list the preparations the Auto-release switch would release when it is turned on
    getQaDispatchSequences: defineQuery(z.object({}), () => zql.patientTherapySequence
        .where('status', '=', PatientTherapySequenceStatus.enum.qa_dispatch)
        .where('deletedAt', 'IS', null)),
    getSequenceById: defineQuery(z.string(), ({ args }) => withSequenceGraph()
        // the drawer lists the protocol's other prescriptions; a reprint does not need that history
        .related('patientProtocol', patProtocol => patProtocol.related('therapySequence', seq => seq
        .related('applicationForm')
        .related('carrierSolution')
        .related('medicine', getSequenceMedicines)
        .orderBy('therapyDate', 'asc')
        .orderBy('time', 'asc')
        .where('deletedAt', 'IS', null)))
        .where('id', args)
        .where('deletedAt', 'IS', null)
        .one()),
    /** the cleanroom actor snapshot for a prescription, so an interrupted preparation resumes at its step */
    getCleanroomState: defineQuery(z.string(), ({ args }) => zql.cleanroomStates.where('therapySequenceId', '=', args).where('deletedAt', 'IS', null).one()),
    /** the same graph for a set of prescriptions, for the Reports reprints (CY-87) */
    getSequencesByIds: defineQuery(z.array(z.string()), ({ args }) => withSequenceGraph({ latestOnly: false }).where('id', 'IN', args).where('deletedAt', 'IS', null)),
    /** prescriptions sharing a prescription number, to match a scanned preparation label (CY-216) */
    getSequencesByInternalId: defineQuery(z.string(), ({ args }) => zql.patientTherapySequence.where('internalId', args).where('deletedAt', 'IS', null).related('patient')),
    /** everything the preparation label prints, in one query */
    getPreparationLabelSource: defineQuery(z.string(), ({ args }) => withPreparationLabelGraph({ latestOnly: false }).where('id', args).where('deletedAt', 'IS', null).one()),
    /** the same graph for a set of prescriptions, for the Reports reprints (CY-87) */
    getPreparationLabelSources: defineQuery(z.array(z.string()), ({ args }) => withPreparationLabelGraph({ latestOnly: false }).where('id', 'IN', args).where('deletedAt', 'IS', null)),
    /** status-change log of a sequence; the COMPLETE_PRODUCTION entry gives produced at / prepared by */
    getSequenceStatusLogs: defineQuery(z.string(), ({ args }) => withStatusLogs().where('resourceId', '=', args)),
    /** the same status logs for a set of prescriptions, for the Reports reprints (CY-87) */
    getSequenceStatusLogsByIds: defineQuery(z.array(z.string()), ({ args }) => withStatusLogs().where('resourceId', 'IN', args)),
    getSequenceActivitiesById: defineQuery(z.object({ sequenceId: z.string(), medicineIds: z.array(z.string()) }), ({ args }) => {
        return zql.eventLogs
            .related('actor', actor => actor.related('groups'))
            .where(({ or, and, cmp }) => or(and(cmp('resource', '=', EventLogResources.patientTherapySequence), cmp('resourceId', '=', args.sequenceId)), and(cmp('resource', '=', EventLogResources.patientTherapySequenceMedicine), cmp('resourceId', 'IN', args.medicineIds))))
            .orderBy('performedAt', 'desc');
    }),
    getOrderById: defineQuery(z.string(), ({ args }) => {
        return zql.orders
            .where('id', args)
            .related('productionLocation')
            .related('sequences', seq => seq
            .related('applicationForm')
            .related('carrierSolution')
            .related('consumables')
            .related('medicine', getSequenceMedicines))
            .related('batches', batch => batch.related('batch'))
            .one();
    }),
    listOrders: defineQuery(ListOrdersInput, ({ args }) => {
        let q = zql.orders.where('deletedAt', 'IS', null).related('sequences', s => s
            .related('patient')
            .related('carrierSolution')
            .related('medicine', getSequenceMedicines)
            .related('cleanroomState')
            .where(({ and, cmp }) => {
            const cond = [cmp('deletedAt', 'IS', null), cmp('status', PatientTherapySequenceStatus.enum.in_production)];
            if (args.filters?.therapyDateFrom)
                cond.push(cmp('therapyDate', '>=', args.filters.therapyDateFrom));
            return and(...cond);
        }));
        if (args.filters) {
            const { productionLocationId, therapyDateFrom, dispatchMode } = args.filters;
            if (productionLocationId?.length)
                q = q.where('productionLocationId', 'IN', productionLocationId);
            if (therapyDateFrom)
                q = q.whereExists('sequences', s => s.where('therapyDate', '>=', therapyDateFrom));
            if (dispatchMode === DispatchMode.enum['smart-compounder']) {
                q = q.whereExists('sequences', s => s.where('compoundedByMachine', '=', true));
            }
            else if (dispatchMode === DispatchMode.enum.manual) {
                // Manual orders have no smart-compounder sequences.
                q = q.whereExists('sequences', s => s.where(({ or, cmp }) => or(cmp('compoundedByMachine', '=', false), cmp('compoundedByMachine', 'IS', null))));
            }
        }
        q = applyCursor(q, { cursor: args.cursor, limit: args.limit, sort: args.sort });
        return q;
    }),
};
//# sourceMappingURL=patients-therapies.queries.js.map